import { useEffect, useMemo, useState } from 'react'
import { isEmpty } from 'lodash'
import { Autocomplete, Box, Container, Grid, TextField } from '@mui/material'
import dayjs from 'dayjs'
import yaml from 'js-yaml'
import Drawer from 'components/drawer/detail'
import Loading from 'components/loading/loading'
import BackdropImage from 'components/backdropImage/backdropImage'
import type { DetailStateProps, ErrorProps } from 'modules/types'
import { useGetPushNotificationDetailQuery } from 'store/pushNotification'
import GlobalStyle from 'modules/styles'
import GLOBAL from 'modules/global'
import Notification from 'components/notification'
import DrawerStyle from './style'

const Detail = ({ open, id, onClose }: DetailStateProps & { id: string | number }) => {
  if (!open) return <Drawer open={open} onClose={onClose} />

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [previewBackdrop, setPreviewBackdrop] = useState<boolean>(false)

  const { data, isFetching, isLoading, isError, error } = useGetPushNotificationDetailQuery(id)

  const loading = !data && (isLoading || isFetching)

  const formatDate = (val: string): string =>
    !isEmpty(val) ? dayjs(val).format('YYYY/MM/DD HH:mm:ss') : ''

  const name = (data && data.name) || '-'
  const airingTime = (data && formatDate(data.airingTime)) || '-'
  const member = (data && data.member?.length > 0 && data.member) || [
    { memberId: 0, memberName: '-' }
  ]
  const headline = (data && data.headline) || '-'
  const subHeadline = (data && data.subHeadline) || '-'
  const carModel = (data && data.carModel?.length > 0 && data.carModel) || [
    { carModelId: 0, carModelName: '-' }
  ]
  const carModelYear = (data && data.carModelYear) || ['-']
  const ctaUrl = (data && data.ctaUrl) || '-'

  const gridDataFirstLabel = [
    'Push Notification Name',
    'Airing Time',
    'Member Tier Target',
    'Headline',
    'SubHeadlie',
    'Car Model',
    'Model Year',
    'CTA Url',
    'Segmentation'
  ]

  useEffect(() => {
    if (isError) {
      setIsOpen(true)
    }
  }, [isError])

  const segmentationObj = useMemo(() => {
    const obj = GLOBAL.deepParseJSON(data?.segmentationJson || '{}')
    const humanizedData = GLOBAL.humanizeKeys(obj)
    const yamlString = yaml.dump(humanizedData) as string
    return yamlString
  }, [data?.segmentationJson])

  return (
    <>
      <Drawer open={open} title='Push Notification Detail' onClose={onClose}>
        <Container {...DrawerStyle.Container}>
          {loading && <Loading />}
          {!loading && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    variant='outlined'
                    label={gridDataFirstLabel[0]}
                    value={name}
                    inputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    variant='outlined'
                    label={gridDataFirstLabel[1]}
                    value={airingTime}
                    inputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    variant='outlined'
                    label={gridDataFirstLabel[3]}
                    value={headline}
                    inputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    variant='outlined'
                    label={gridDataFirstLabel[4]}
                    value={subHeadline}
                    inputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>

                {!isEmpty(data?.segmentationJson) ? (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        multiline
                        rows={10}
                        variant='outlined'
                        label={gridDataFirstLabel[8]}
                        value={segmentationObj}
                        inputProps={{ readOnly: true }}
                        fullWidth
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        fullWidth
                        readOnly
                        options={member}
                        getOptionLabel={(list) => list.memberName}
                        isOptionEqualToValue={(option, value) =>
                          option && value ? option.memberId == value.memberId : false
                        }
                        ListboxProps={GlobalStyle.ListBox}
                        renderOption={(props, item) => (
                          <li {...props} key={item.memberId}>
                            {item.memberName}
                          </li>
                        )}
                        value={member}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={gridDataFirstLabel[2]}
                            required
                            InputProps={{
                              ...params.InputProps,
                              readOnly: true
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        fullWidth
                        readOnly
                        options={carModel}
                        getOptionLabel={(list) => list.carModelName}
                        isOptionEqualToValue={(option, value) =>
                          option && value ? option.carModelId == value.carModelId : false
                        }
                        ListboxProps={GlobalStyle.ListBox}
                        renderOption={(props, item) => (
                          <li {...props} key={item.carModelId}>
                            {item.carModelName}
                          </li>
                        )}
                        value={carModel}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={gridDataFirstLabel[5]}
                            required
                            InputProps={{
                              ...params.InputProps,
                              readOnly: true
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        fullWidth
                        readOnly
                        options={carModelYear.map((item) => item.toString())}
                        getOptionLabel={(list) => list.toString()}
                        isOptionEqualToValue={(option, value) =>
                          option && value ? option == value : false
                        }
                        ListboxProps={GlobalStyle.ListBox}
                        renderOption={(props, item) => (
                          <li {...props} key={item}>
                            {item}
                          </li>
                        )}
                        value={carModelYear.map((item) => item.toString())}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={gridDataFirstLabel[6]}
                            required
                            InputProps={{
                              ...params.InputProps,
                              readOnly: true
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <TextField
                    variant='outlined'
                    label={gridDataFirstLabel[7]}
                    value={ctaUrl}
                    inputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>

                {data?.imageUrl && (
                  <Grid item xs={12}>
                    <Box {...DrawerStyle.Image}>
                      <BackdropImage
                        open={previewBackdrop}
                        onOpen={() => setPreviewBackdrop(true)}
                        onClose={() => setPreviewBackdrop(false)}
                      >
                        <img
                          src={data.imageUrl}
                          alt=''
                          style={{ height: '300px', width: '505px', objectFit: 'contain' }}
                        />
                      </BackdropImage>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Container>
      </Drawer>

      <Notification
        open={isOpen}
        onClose={() => setIsOpen(false)}
        isError={true}
        message={GLOBAL.returnExceptionMessage(isError, error as ErrorProps)}
      />
    </>
  )
}

export default Detail
